import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, useTheme } from '@mui/material/styles';
import { Box, Stack, Drawer } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Scrollbar from '../../../components/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';

// routes
import { PATH_DASHBOARD, PATH_ADMIN } from '../../../routes/paths';
// components
// import Label from '../../../components/Label';
import SvgIconStyle from '../../../components/SvgIconStyle';
//


import NavbarAccount from './NavbarAccount';
import CollapseButton from './CollapseButton';
import useAuth from '../../../hooks/useAuth';


// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const theme = useTheme();
  const { user, logout, setting } = useAuth();
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();
  const getIcon = (name) => <SvgIconStyle src={`/icons/${name}.svg`} sx={{ width: 1, height: 1 }} />;
  const ICONS = {
    user: getIcon('ic_user'),
    transaction: getIcon('transaction'),
    buy_cable: getIcon('Habukhan_cable'),
    calendar: getIcon('ic_calendar'),
    buy_data: getIcon('Habukhan_data'),
    buy_airtime: getIcon('Habukhan_phone'),
    dashboard: getIcon('Habukhan_home'),
    fund: getIcon('fund'),
    phone: getIcon('phone'),
    stock: getIcon('stock'),
    setting: getIcon('setting'),
    wallet: getIcon('wallet'),
    cal: getIcon('cal'),
    price: getIcon('price'),
    logout: getIcon('ic_logout')
  };


  // nav bar — matches screenshot exactly
  const navConfig = [
    {
      subheader: 'account',
      items: [
        { title: 'Dashboard', path: PATH_DASHBOARD.general.app, icon: ICONS.dashboard },
        { title: 'Bill Payments', path: PATH_DASHBOARD.general.bill_payments, icon: ICONS.fund },
        { title: 'Transaction History', path: PATH_DASHBOARD.general.transactions, icon: ICONS.transaction },
        { title: 'Airtime 2 Cash', path: PATH_DASHBOARD.general.cash, icon: ICONS.phone },
        { title: 'Developers API', path: '/documentation/home', icon: ICONS.stock },
        { title: 'My Accounts', path: PATH_DASHBOARD.user.account, icon: ICONS.user },
        { title: 'Help & Support', path: PATH_DASHBOARD.general.support, icon: ICONS.cal },
      ],
    }
  ];

  const customerTrans = [
    {
      subheader: 'customer care',
      items: [
        {
          title: 'Transaction',
          icon: ICONS.transaction,
          path: PATH_DASHBOARD.customer.root,
          children: [
            { title: 'Transaction Summary', path: PATH_DASHBOARD.customer.history },
            { title: 'Deposit Summary', path: PATH_DASHBOARD.customer.deposit },
            { title: 'Manual Funding', path: PATH_DASHBOARD.customer.manualfunding },
            { title: 'Data Transaction', path: PATH_DASHBOARD.customer.data },
            { title: 'Airtime Transaction', path: PATH_DASHBOARD.customer.airtime },
            { title: 'Cable Transaction', path: PATH_DASHBOARD.customer.cable },
            { title: 'Electricity Transaction', path: PATH_DASHBOARD.customer.bill },
            { title: 'Bulk SMS Transaction', path: PATH_DASHBOARD.customer.bulksms },
            { title: 'Airtime 2 Cash', path: PATH_DASHBOARD.customer.airtimecash },
            { title: 'Result Checker', path: PATH_DASHBOARD.customer.result },
          ],
        },
        { path: PATH_DASHBOARD.credituser, title: 'Credit / Debit', icon: ICONS.wallet },
        ...(user?.type === 'ADMIN' ? [{ title: 'Admin Panel', path: PATH_ADMIN.general.app, icon: ICONS.setting }] : []),
      ],
    },
  ];

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error(err);
    }
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          ...(isCollapse && { alignItems: 'center' }),
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Logo isLight={!pathname.includes('/secure')} />
          {isDesktop && !isCollapse && (
            <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
          )}
        </Stack>
        {/* NavbarAccount hidden as per user request */}
      </Stack>

      <NavSectionVertical navConfig={navConfig} isCollapse={isCollapse} isDark={false} />
      {(user?.type === 'CUSTOMER' || user?.type === 'ADMIN') && (
        <NavSectionVertical navConfig={customerTrans} isCollapse={isCollapse} isDark={false} />
      )}

      <Box sx={{ flexGrow: 1 }} />

      {/* Logout at the bottom */}
      <Box
        onClick={handleLogout}
        sx={{
          px: 2.5,
          pb: 3,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          color: 'primary.main',
          '&:hover': { color: 'primary.dark' },
          transition: 'color 0.2s',
        }}
      >
        <Box sx={{ width: 20, height: 20 }}>{ICONS.logout}</Box>
        {!isCollapse && <Box component="span" sx={{ fontSize: 14, fontWeight: 600 }}>Logout</Box>}
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
      }}
    >
      {!isDesktop && (
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              bgcolor: 'background.default',
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: pathname.includes('/secure') ? 'dashed' : 'none',
              bgcolor: 'background.default',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
